import { useCallback, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Plus, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"

/** Known-key hint — how to render this key's value in the inline UI. */
export type KeyValueFieldHint = {
  /** Value type — selects the control to render (boolean, number, string, text). */
  type: "boolean" | "number" | "string" | "text"
  /** Human label. Defaults to the key itself when omitted. */
  label?: string
  /** One-line helper shown under the field. */
  description?: string
}

export type KeyValueSchema = Record<string, KeyValueFieldHint>

type KeyValueOverridesProps = {
  /** The current dict being edited. Empty-object-valid. */
  value: Record<string, unknown>
  /** Called whenever any row is added / removed / changed. */
  onChange: (next: Record<string, unknown>) => void
  /**
   * Known-key hints. Keys present here render with typed controls; keys not
   * listed fall through to a generic string input. Callers can pass an empty
   * object to render everything as strings.
   */
  schema?: KeyValueSchema
  /** Controls label text for the whole component. */
  label?: string
  /** One-line helper for the whole component. */
  description?: string
  /** Disable all inputs. */
  disabled?: boolean
  /**
   * When true, the "Add" button surfaces a dropdown of known (unused)
   * schema keys for quick discovery. Defaults to true.
   */
  offerKnownKeys?: boolean
}

/**
 * A generic key-value sub-editor for small dict-shaped config objects.
 *
 * This is the "Form-tab escape hatch" that sits alongside dedicated form
 * controls for well-known fields: routes' ``backend_overrides``, features'
 * ``config``, tools' override dicts, MCP ``headers``, and so on. Admins
 * can:
 *
 * - Toggle / type into typed controls for keys the caller supplied in
 *   ``schema`` — e.g. ``disableWebSearch`` renders as a Switch because the
 *   route dialog's schema marks it ``type: "boolean"``.
 * - Add ad-hoc string keys with "+ Add" for forward-compatible extensions.
 * - Remove any key with the × button.
 *
 * For anything more complex (nested objects, arrays) the Raw JSON tab is
 * the right tool. This component intentionally doesn't try to be a full
 * JSON editor.
 *
 * Value handling:
 * - ``boolean`` / ``number`` / ``string`` — typed controls, typed writeback.
 * - ``text`` — multi-line textarea, string writeback.
 * - Unknown keys — string input, string writeback. The component
 *   deliberately does NOT JSON-parse string values; if an admin needs to
 *   store a number or boolean under an unknown key they should mark the
 *   schema or use Raw mode. This avoids the "my config broke because my
 *   literal string 'true' became the boolean true" class of bugs.
 */
export function KeyValueOverrides({
  value,
  onChange,
  schema = {},
  label,
  description,
  disabled = false,
  offerKnownKeys = true,
}: KeyValueOverridesProps) {
  const { t } = useTranslation()

  // Stable iteration order: schema-known keys first (in schema insertion
  // order), then unknown keys in their stored order. This makes the form
  // feel consistent across loads instead of reshuffling on every save.
  const entries = useMemo(() => {
    const seen = new Set<string>()
    const rows: Array<{ key: string; known: boolean }> = []
    for (const key of Object.keys(schema)) {
      if (key in value) {
        rows.push({ key, known: true })
        seen.add(key)
      }
    }
    for (const key of Object.keys(value)) {
      if (!seen.has(key)) {
        rows.push({ key, known: key in schema })
      }
    }
    return rows
  }, [value, schema])

  const unusedKnownKeys = useMemo(
    () => Object.keys(schema).filter((k) => !(k in value)),
    [schema, value]
  )

  const setKey = useCallback(
    (oldKey: string, newKey: string): boolean => {
      if (oldKey === newKey) return true
      // Reject collisions: renaming to an existing key would silently
      // overwrite the other entry because the rebuilt object can only hold
      // one property per name. The caller is responsible for surfacing
      // the rejection to the user (e.g. reverting the input).
      if (newKey in value) {
        toast.error(
          t("adminDashboard.keyValueOverrides.duplicateKey", {
            defaultValue: "Key '{{key}}' already exists",
            key: newKey,
          })
        )
        return false
      }
      const next: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(value)) {
        next[k === oldKey ? newKey : k] = v
      }
      onChange(next)
      return true
    },
    [value, onChange, t]
  )

  const setValue = useCallback(
    (key: string, next: unknown) => {
      onChange({ ...value, [key]: next })
    },
    [value, onChange]
  )

  const removeKey = useCallback(
    (key: string) => {
      const next = { ...value }
      delete next[key]
      onChange(next)
    },
    [value, onChange]
  )

  const addKnownKey = useCallback(
    (key: string) => {
      // Callers only invoke this with keys drawn from ``unusedKnownKeys``,
      // which is itself a filter over ``Object.keys(schema)``. So the schema
      // entry is guaranteed to exist.
      const hintType = schema[key].type
      let initial: unknown = ""
      if (hintType === "boolean") initial = false
      else if (hintType === "number") initial = 0
      onChange({ ...value, [key]: initial })
    },
    [value, onChange, schema]
  )

  const addAdHocKey = useCallback(() => {
    // Find a unique placeholder key like "key", "key2", "key3" …
    let candidate = "key"
    let n = 2
    while (candidate in value) {
      candidate = `key${n}`
      n++
    }
    onChange({ ...value, [candidate]: "" })
  }, [value, onChange])

  return (
    <div className="space-y-2">
      {label || description ? (
        <div className="space-y-1">
          {label ? <Label>{label}</Label> : null}
          {description ? <p className="text-muted-foreground text-xs">{description}</p> : null}
        </div>
      ) : null}

      {entries.length === 0 ? (
        <div className="border-border text-muted-foreground rounded-md border border-dashed px-3 py-4 text-center text-xs">
          {t("adminDashboard.keyValueOverrides.empty", {
            defaultValue: "No overrides. Add one below.",
          })}
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map(({ key, known }) => {
            const hint = known ? schema[key] : undefined
            const currentValue = value[key]
            return (
              <KeyValueRow
                key={key}
                disabled={disabled}
                fieldKey={key}
                fieldValue={currentValue}
                hint={hint}
                onKeyCommit={(newKey) => setKey(key, newKey)}
                onRemove={() => removeKey(key)}
                onValueChange={(nextVal) => setValue(key, nextVal)}
              />
            )
          })}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button disabled={disabled} size="sm" type="button" variant="outline" onClick={addAdHocKey}>
          <Plus className="size-4" />
          {t("adminDashboard.keyValueOverrides.addCustom", {
            defaultValue: "Add custom key",
          })}
        </Button>
        {offerKnownKeys && unusedKnownKeys.length > 0 ? (
          <>
            <span className="text-muted-foreground text-xs">
              {t("adminDashboard.keyValueOverrides.knownKeysLabel", {
                defaultValue: "Or add a known key:",
              })}
            </span>
            {unusedKnownKeys.map((key) => (
              <Button
                key={key}
                disabled={disabled}
                size="sm"
                type="button"
                variant="ghost"
                onClick={() => addKnownKey(key)}
              >
                <Plus className="size-3.5" />
                {schema[key].label ?? key}
              </Button>
            ))}
          </>
        ) : null}
      </div>
    </div>
  )
}

type KeyValueRowProps = {
  fieldKey: string
  fieldValue: unknown
  hint?: KeyValueFieldHint
  disabled: boolean
  /**
   * Called on blur with the typed key. Returns true when the rename was
   * accepted (parent state updated) and false when rejected (e.g.
   * collision); on rejection the row reverts to the original key so the
   * user sees the change didn't apply.
   */
  onKeyCommit: (next: string) => boolean
  onValueChange: (next: unknown) => void
  onRemove: () => void
}

function KeyValueRow({
  fieldKey,
  fieldValue,
  hint,
  disabled,
  onKeyCommit,
  onValueChange,
  onRemove,
}: KeyValueRowProps) {
  const isKnown = Boolean(hint)
  const hintLabel = hint?.label ?? fieldKey

  // Local edit state so users can type a partial / temporarily-conflicting
  // key without us spamming validation on every keystroke. The committed
  // parent state only changes on blur (and only when the rename is
  // accepted by ``onKeyCommit``).
  const [draftKey, setDraftKey] = useState(fieldKey)
  useEffect(() => {
    setDraftKey(fieldKey)
  }, [fieldKey])

  return (
    <div className="grid grid-cols-12 items-start gap-2">
      <div className="col-span-5">
        <Input
          aria-label="Key"
          className="font-mono text-xs"
          disabled={disabled || isKnown}
          value={draftKey}
          onChange={(event) => setDraftKey(event.target.value)}
          onBlur={() => {
            const trimmed = draftKey.trim()
            if (!trimmed || trimmed === fieldKey) {
              setDraftKey(fieldKey)
              return
            }
            const accepted = onKeyCommit(trimmed)
            if (!accepted) {
              setDraftKey(fieldKey)
            }
          }}
        />
        {isKnown ? <p className="text-muted-foreground mt-1 text-[10px]">{hintLabel}</p> : null}
      </div>
      <div className="col-span-6">
        <KeyValueValueControl
          disabled={disabled}
          fieldValue={fieldValue}
          hint={hint}
          onChange={onValueChange}
        />
        {hint?.description ? (
          <p className="text-muted-foreground mt-1 text-[10px]">{hint.description}</p>
        ) : null}
      </div>
      <div className="col-span-1 flex justify-end">
        <Button
          aria-label="Remove"
          disabled={disabled}
          size="icon"
          type="button"
          variant="ghost"
          onClick={onRemove}
        >
          <X className="size-4" />
        </Button>
      </div>
    </div>
  )
}

function KeyValueValueControl({
  fieldValue,
  hint,
  disabled,
  onChange,
}: {
  fieldValue: unknown
  hint?: KeyValueFieldHint
  disabled: boolean
  onChange: (next: unknown) => void
}) {
  if (hint?.type === "boolean") {
    return (
      <div className="flex h-9 items-center">
        <Switch checked={Boolean(fieldValue)} disabled={disabled} onCheckedChange={onChange} />
      </div>
    )
  }

  if (hint?.type === "number") {
    const asNumber =
      typeof fieldValue === "number" ? fieldValue : Number.parseFloat(String(fieldValue ?? ""))
    return (
      <Input
        disabled={disabled}
        type="number"
        value={Number.isFinite(asNumber) ? asNumber : ""}
        onChange={(event) => {
          const raw = event.target.value
          if (raw === "") {
            onChange(0)
            return
          }
          const parsed = Number.parseFloat(raw)
          onChange(Number.isFinite(parsed) ? parsed : 0)
        }}
      />
    )
  }

  if (hint?.type === "text") {
    return (
      <Textarea
        disabled={disabled}
        rows={3}
        value={typeof fieldValue === "string" ? fieldValue : JSON.stringify(fieldValue)}
        onChange={(event) => onChange(event.target.value)}
      />
    )
  }

  // Unknown or explicit "string": string control. Complex values (objects /
  // arrays) are preserved read-only via JSON.stringify and a disabled input,
  // nudging the admin to use Raw mode for structural edits.
  const isComplex =
    fieldValue !== null && (typeof fieldValue === "object" || Array.isArray(fieldValue))
  if (isComplex) {
    return <Input disabled className="font-mono text-xs" value={JSON.stringify(fieldValue)} />
  }

  return (
    <Input
      disabled={disabled}
      value={typeof fieldValue === "string" ? fieldValue : String(fieldValue ?? "")}
      onChange={(event) => onChange(event.target.value)}
    />
  )
}
